import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  Signal,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { NgStyle } from '@angular/common';
import { TourService } from '@shared/components/tour/tour.service';
import { TourPosition } from '@shared/components/tour/tour.types';

/** Clear space (px) between the highlighted element and the ring (internal gap). */
const RING_INNER_GAP = 8;
/** Thickness (px) of the green ring line. */
const RING_WIDTH = 2;
/** Clear space (px) between the ring and the dimmed area (external gap). */
const RING_OUTER_GAP = 8;
/** Gap (px) between the dimmed area edge and the tooltip. */
const TOOLTIP_GAP = 14;
/** Tooltip width (px) used to clamp it inside the viewport. */
const TOOLTIP_WIDTH = 280;
/** Minimum margin (px) kept from the viewport edges. */
const VIEWPORT_MARGIN = 12;

/**
 * Reusable guided-tour overlay. Render it once near the app root; it reacts to
 * {@link TourService} and highlights the current step's element with a spotlight
 * and a tooltip. Targets are resolved per step (selector or direct element).
 */
@Component({
  selector: 'app-tour',
  templateUrl: './tour.component.html',
  styleUrls: ['./tour.component.scss'],
  standalone: true,
  imports: [NgStyle],
})
export class TourComponent implements AfterViewInit, OnDestroy {
  public readonly tour = inject(TourService);

  private readonly overlay: Signal<ElementRef<HTMLElement> | undefined> = viewChild('overlay');
  private readonly targetRect = signal<DOMRect | null>(null);
  private rafId: number | null = null;
  private lastWidth = window.innerWidth;

  /** Effective placement for the active step (defaults to `bottom`). */
  private readonly position = computed<TourPosition>(() => this.tour.currentStep()?.position ?? 'bottom');

  /** Total distance (px) from the element edge to the dimmed area edge. */
  private static readonly OUTER = RING_INNER_GAP + RING_WIDTH + RING_OUTER_GAP;

  /** Ring box: sits RING_INNER_GAP beyond the element (clear internal gap). */
  public readonly spotlightRingStyle = computed<Record<string, string>>(() => {
    const rect = this.targetRect();
    if (!rect) return { display: 'none' };
    return {
      top: `${rect.top - RING_INNER_GAP}px`,
      left: `${rect.left - RING_INNER_GAP}px`,
      width: `${rect.width + RING_INNER_GAP * 2}px`,
      height: `${rect.height + RING_INNER_GAP * 2}px`,
    };
  });

  /** Dim box: larger than the ring, leaving a clear external gap before the dim. */
  public readonly spotlightDimStyle = computed<Record<string, string>>(() => {
    const rect = this.targetRect();
    if (!rect) return { display: 'none' };
    const out = TourComponent.OUTER;
    return {
      top: `${rect.top - out}px`,
      left: `${rect.left - out}px`,
      width: `${rect.width + out * 2}px`,
      height: `${rect.height + out * 2}px`,
    };
  });

  public readonly tooltipStyle = computed<Record<string, string>>(() => {
    const rect = this.targetRect();
    if (!rect) return { display: 'none' };

    const pos = this.position();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const vw = window.innerWidth;

    const offset = TourComponent.OUTER + TOOLTIP_GAP;

    const half = TOOLTIP_WIDTH / 2;
    const clampedX = Math.min(Math.max(centerX, half + VIEWPORT_MARGIN), vw - half - VIEWPORT_MARGIN);

    switch (pos) {
      case 'top':
        return { top: `${rect.top - offset}px`, left: `${clampedX}px`, transform: 'translate(-50%, -100%)' };
      case 'left':
        return { top: `${centerY}px`, left: `${rect.left - offset}px`, transform: 'translate(-100%, -50%)' };
      case 'right':
        return { top: `${centerY}px`, left: `${rect.right + offset}px`, transform: 'translateY(-50%)' };
      case 'bottom':
      default:
        return { top: `${rect.bottom + offset}px`, left: `${clampedX}px`, transform: 'translateX(-50%)' };
    }
  });

  private readonly recomputeOnStep = effect(() => {
    const active = this.tour.isActive();
    this.tour.currentStep();
    untracked(() => {
      if (active) {
        this.revealAndMeasure();
      } else {
        this.cancelRaf();
        this.targetRect.set(null);
      }
    });
  });

  public ngAfterViewInit(): void {
    const el = this.overlay()?.nativeElement;
    if (el) document.body.appendChild(el);
  }

  public ngOnDestroy(): void {
    this.cancelRaf();
    const el = this.overlay()?.nativeElement;
    if (el?.parentNode) el.parentNode.removeChild(el);
    this.tour.stop();
  }

  @HostListener('window:scroll')
  protected onScroll(): void {
    if (this.tour.isActive() && this.targetRect()) this.reposition();
  }

  @HostListener('window:resize')
  protected onResize(): void {
    const width = window.innerWidth;
    const widthChanged = width !== this.lastWidth;
    this.lastWidth = width;
    if (!this.tour.isActive()) return;
    if (widthChanged) {
      this.tour.stop();
    } else if (this.targetRect()) {
      this.reposition();
    }
  }

  public next(): void {
    this.tour.next();
  }

  public previous(): void {
    this.tour.previous();
  }

  public skip(): void {
    this.tour.dismiss();
  }

  /** Resolves the current step's target element (direct element or selector/id). */
  private resolveEl(): HTMLElement | null {
    const step = this.tour.currentStep();
    if (!step) return null;
    if (step.element) return step.element;
    if (!step.selector) return null;
    return document.querySelector<HTMLElement>(step.selector) ?? document.getElementById(step.selector);
  }

  /** Smooth-scrolls the target into view, then reveals the tour once the scroll settles. */
  private revealAndMeasure(): void {
    const el = this.resolveEl();
    if (!el || !el.isConnected) {
      this.targetRect.set(null);
      return;
    }
    el.scrollIntoView({ block: 'center', inline: 'center' });
    this.measureWhenSettled(el);
  }

  /** Polls until the (smooth) scroll stops moving the target, then measures and reveals it. */
  private measureWhenSettled(el: HTMLElement): void {
    this.cancelRaf();
    let last: DOMRect | null = null;
    let stable = 0;
    let frames = 0;
    const tick = (): void => {
      if (!this.tour.isActive() || !el.isConnected) {
        this.rafId = null;
        return;
      }
      const rect = el.getBoundingClientRect();
      if (last && Math.abs(rect.top - last.top) < 0.5 && Math.abs(rect.left - last.left) < 0.5) {
        stable++;
      } else {
        stable = 0;
      }
      last = rect;
      if (stable >= 3 || frames >= 90) {
        this.rafId = null;
        this.targetRect.set(rect);
        return;
      }
      frames++;
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  /** Re-measures the target without scrolling; stops the tour if it detached. */
  private reposition(): void {
    const el = this.resolveEl();
    if (!el || !el.isConnected) {
      this.targetRect.set(null);
      if (this.tour.isActive()) this.tour.stop();
      return;
    }
    this.cancelRaf();
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.targetRect.set(el.getBoundingClientRect());
    });
  }

  private cancelRaf(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }
}
